import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from 'react';

interface DragContextType {
  draggedId: number | null;
  setDraggedId: (id: number | null) => void;
  dragOverId: number | null;
  setDragOverId: (id: number | null) => void;
  onDrop?: (draggedId: number, targetId: number) => void;
}

const DragContext = createContext<DragContextType>({
  draggedId: null,
  setDraggedId: () => {},
  dragOverId: null,
  setDragOverId: () => {}
});

interface DragContextProviderProps {
  children: ReactNode;
  onDrop?: (draggedId: number, targetId: number) => void;
}

export const DragContextProvider = ({
  children,
  onDrop
}: DragContextProviderProps) => {
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const value = useMemo(
    () => ({ draggedId, setDraggedId, dragOverId, setDragOverId, onDrop }),
    [draggedId, dragOverId, onDrop]
  );

  return <DragContext.Provider value={value}>{children}</DragContext.Provider>;
};

export function useDragContext(): DragContextType {
  const context = useContext(DragContext);
  if (context === undefined) {
    throw new Error('useDragContext must be used within a DragContextProvider');
  }
  return context;
}
